/* src/components/ui/NotificationTray.js */
import React from 'react'; // Core React import
import { useNotifications } from '../../contexts/NotificationContext';
import { formatDateTime } from '../dashboard/utils/formatters'; // Utility for date formatting

// Import Font Awesome components
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,           // For empty state icon
  faUserPlus,       // Friend request icon
  faHandshake,      // Friend accept icon
  faCheckDouble,    // Mark all read icon
  faComment,        // New comment icon
  faExclamationCircle // Error icon
} from '@fortawesome/free-solid-svg-icons';

/**
 * Get FontAwesome icon based on notification type
 */
const getNotificationIcon = (type) => {
  switch (type) {
    case 'friend_request':
      return faUserPlus;
    case 'friend_accept':
      return faHandshake;
    case 'new_comment': // Example - adjust as needed
      return faComment;
    // Add more specific icons as your notification types grow
    default:
      return faBell; // Default fallback icon
  }
};

/**
 * NotificationTray Component
 * Displays the list of notifications within a popover/dropdown.
 * Assumes visibility is controlled by the parent component.
 * Relies on event propagation being stopped correctly to avoid premature closing.
 *
 * Props:
 * - navigate: {function} The navigate function from react-router-dom (passed from parent).
 * - onClose: {function} Callback function provided by the parent to signal the tray should close.
 */
const NotificationTray = ({ navigate, onClose }) => {
  // Get notification data and actions from context
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    markNotificationRead,
    markAllNotificationsRead
  } = useNotifications(); // Hook must be called within a NotificationProvider

  /**
   * Handles clicking on an individual notification item.
   * Marks as read (if unread), navigates (if applicable), and then calls onClose.
   * Made async to ensure markNotificationRead completes before potential navigation/closing.
   */
  const handleNotificationClick = async (notification) => {
    console.log(`NotificationTray: Handling click for notification ID: ${notification.id}, type: ${notification.type}`);
    try {
      // Mark as read *before* navigating or closing, only if currently unread
      if (!notification.read) {
        console.log(`NotificationTray: Marking notification ${notification.id} as read.`);
        // Await the async operation from the context
        await markNotificationRead(notification.id);
        console.log(`NotificationTray: markNotificationRead completed for ${notification.id}.`);
      }

      let navigated = false; // Track if navigation happened

      // Navigate based on notification type using the passed navigate function
      if (navigate && typeof navigate === 'function') {
        console.log(`NotificationTray: Attempting navigation for type: ${notification.type}`);
        switch (notification.type) {
          case 'friend_request':
          case 'friend_accept':
            navigate('/friends'); // Navigate to the friends page
            navigated = true;
            console.log("NotificationTray: Navigated to /friends.");
            break;
          case 'new_comment':
             // Example: Navigation logic for comments
             if (notification.relatedEntityId && notification.relatedEntityType) {
                 // TODO: Implement actual navigation based on entity type and ID
                 // const path = `/${notification.relatedEntityType}s/${notification.relatedEntityId}`;
                 // navigate(path);
                 // navigated = true;
                 console.log(`TODO: Implement navigation for ${notification.type} to ${notification.relatedEntityType} ID: ${notification.relatedEntityId}`);
             } else {
                 console.log(`Navigation requested for ${notification.type} but missing related entity info.`);
             }
             break;
          // Add more cases for different notification types and destinations
          default:
            console.log(`No specific navigation configured for notification type: ${notification.type}`);
        }
      } else {
          console.warn('Navigate function was not provided to NotificationTray.');
      }

    } catch (err) {
        console.error("Error processing notification click:", err);
        // Optionally show user feedback here
    } finally {
       // IMPORTANT: Call onClose *after* all processing (including async mark read) is done.
       // This ensures the tray closes only after the interaction is complete.
       if (onClose && typeof onClose === 'function') {
         console.log("NotificationTray: Calling onClose callback.");
         onClose();
       } else {
         console.warn("NotificationTray: onClose callback was not provided or is not a function.");
       }
    }
  };

  /**
   * Handles clicking the "Mark all read" button.
   * Stops event propagation to prevent unintended side effects.
   * Calls the context action to mark all as read.
   * Made async to potentially await the operation if needed.
   */
  const handleMarkAllRead = async (e) => {
    // *** CRITICAL: Stop propagation to prevent the click from reaching parent listeners ***
    e.stopPropagation();
    console.log("NotificationTray: Handling Mark All Read click, stopped propagation.");

    if (unreadCount > 0 && !isLoading) { // Check isLoading too
      console.log("NotificationTray: Calling markAllNotificationsRead context action.");
      try {
        // Await the context action (good practice in case it becomes slow or returns status)
        await markAllNotificationsRead();
        console.log("NotificationTray: markAllNotificationsRead context action finished.");
        // Typically, DO NOT call onClose() here. Let the user see the items update.
      } catch (err) {
        console.error("Error marking all notifications read:", err);
        // Optionally show user feedback
      }
    } else {
        console.log("NotificationTray: Mark All Read clicked, but no unread notifications or already loading.");
    }
  };

  return (
    // Outer container for the tray
    <div
      role="menu" // Accessibility role
      aria-orientation="vertical"
      aria-labelledby="options-menu" // Relates to the button that opens this tray
      className="max-h-[80vh] flex flex-col bg-white shadow-lg rounded-lg border border-gray-200" // Added border, shadow, explicit bg
    >
      {/* Dropdown Header */}
      <div className="px-4 py-3 flex justify-between items-center border-b border-gray-100 bg-gray-50 rounded-t-lg">
        <span className="text-sm font-semibold text-gray-800">Notifications</span>

        {/* "Mark all read" button - visible only if there are unread notifications and not loading */}
        {unreadCount > 0 && ( // Condition remains the same
          <button
            onClick={handleMarkAllRead} // Use the specific async handler
            disabled={isLoading} // Disable while loading/updating
            className="text-xs font-medium text-indigo-600 hover:text-indigo-800 flex items-center transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-indigo-600" // Added focus styles and disabled hover override
          >
            <FontAwesomeIcon icon={faCheckDouble} className="mr-1 h-3 w-3" />
            Mark all read
          </button>
        )}
         {/* Show something if unread count is 0 but notifications exist */}
         {unreadCount === 0 && notifications.length > 0 && !isLoading && (
            <span className="text-xs font-medium text-gray-400">All caught up</span>
         )}
      </div>

      {/* Content Area: Handles Loading, Error, Empty, and List states */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden"> {/* Ensure this container handles scroll */}
        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-8 px-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500"></div>
            <span className="ml-3 text-sm text-gray-500">Loading notifications...</span>
          </div>
        )}

        {/* Error State */}
        {!isLoading && error && (
          <div className="px-4 py-6 text-sm text-red-600 text-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="mr-2" />
            {error || "Failed to load notifications."} {/* Display specific error or default */}
          </div>
        )}

        {/* Empty State (No notifications at all) */}
        {!isLoading && !error && notifications.length === 0 && (
          <div className="px-4 py-6 text-sm text-gray-500 text-center">
            <div className="flex justify-center mb-2">
              <FontAwesomeIcon icon={faBell} className="h-8 w-8 text-gray-300" />
            </div>
            <p className="font-medium">No notifications yet</p>
            <p className="text-xs mt-1">We'll let you know when something comes up.</p>
          </div>
        )}

        {/* Notification List */}
        {!isLoading && !error && notifications.length > 0 && (
          <ul className="divide-y divide-gray-100"> {/* Use ul for semantic list */}
            {notifications.map(notification => (
              <li key={notification.id}> {/* Use li for list items */}
                <button // Use button for clickable list items
                  // *** CRITICAL CHANGE HERE: Stop propagation INLINE ***
                  onClick={(e) => {
                    e.stopPropagation(); // Stop the click from bubbling up!
                    console.log(`NotificationTray: Clicked item ${notification.id} button, stopped propagation.`);
                    handleNotificationClick(notification); // Call the async handler
                  }}
                  className={`
                    w-full text-left block px-4 py-3.5 text-sm cursor-pointer
                    transition-colors duration-150 ease-in-out
                    focus:outline-none focus:bg-gray-100 focus:ring-2 focus:ring-inset focus:ring-indigo-300
                    ${!notification.read
                      ? 'bg-indigo-50 hover:bg-indigo-100 focus:bg-indigo-100' // Distinct unread style
                      : 'bg-white hover:bg-gray-50 focus:bg-gray-100'         // Standard read style
                    }
                  `}
                  role="menuitem" // Role for accessibility
                >
                  <div className="flex items-start"> {/* Use items-start for potentially multi-line messages */}
                    {/* Icon Container */}
                    <div className={`
                      flex-shrink-0 mr-3 mt-0.5 rounded-full h-8 w-8 flex items-center justify-center
                      ${!notification.read ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500'}
                    `}>
                      <FontAwesomeIcon icon={getNotificationIcon(notification.type)} className="h-4 w-4"/>
                    </div>

                    {/* Content Container */}
                    <div className="flex-1 min-w-0"> {/* min-w-0 prevents overflow issues with flex */}
                      {/* Message - Allow wrapping */}
                      <p className={`text-sm break-words ${!notification.read ? 'font-semibold text-gray-900' : 'text-gray-800'}`}>
                        {notification.message || "Notification message missing."} {/* Added fallback */}
                      </p>
                      {/* Timestamp */}
                      <p className={`text-xs mt-1 ${!notification.read ? 'text-indigo-600 font-medium' : 'text-gray-500'}`}>
                         {/* Ensure formatDateTime handles potential invalid/missing dates gracefully */}
                         {notification.createdAt ? formatDateTime(notification.createdAt) : 'Invalid date'}
                      </p>
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default NotificationTray;